"use client";

import React, { Suspense, useCallback, useEffect, useState } from "react";
import { Button, Icon, LoadingSpinner, useSidebar } from "@focal/ui-kit";
import { useAskFocalSidebarStore } from "@/store/askFocalSidebar";
import {
  CHAT_PANEL_HEADER,
  CHAT_PANEL_SURFACE,
  CHAT_RESIZE_HANDLE,
  useChatPanelResize,
} from "@/components/common/chatSurface";
import { useScreenWidth } from "@/hooks/useWindowSize";
import { cn } from "@/lib/utils";

const AskFocalSidebarContent = React.lazy(
  () => import("./AskFocalSidebarContent"),
);

const SidebarHeader = ({ onClose }: { onClose: () => void }) => (
  <div className={CHAT_PANEL_HEADER}>
    <div className="flex items-center gap-1">
      <Icon name="AiIcon" className="size-5 text-blue-600" />
      <span className="text-sm font-medium text-typography-secondary">
        Ask Focal
      </span>
    </div>
    <Button
      variant="ghost"
      size="icon"
      onClick={onClose}
      aria-label="Close sidebar"
      data-cy="ask-focal-sidebar-close"
    >
      <Icon name="CloseIcon" />
    </Button>
  </div>
);

// At narrow desktop widths (1000–1200px), if the user drags the chat sidebar
// so it occupies more than this fraction of the viewport AND the left main
// sidebar is still expanded, the main sidebar collapses to give the chat
// room. Threshold + viewport range chosen empirically: outside this band the
// main sidebar collapse isn't needed (≥1200) or the chat is in overlay mode
// (<1024) and the left sidebar already auto-collapses.
const AUTO_COLLAPSE_MAIN_SIDEBAR_VIEWPORT_MIN = 1000;
const AUTO_COLLAPSE_MAIN_SIDEBAR_VIEWPORT_MAX = 1200;
const AUTO_COLLAPSE_MAIN_SIDEBAR_RATIO = 0.4;

export const AskFocalSidebar = () => {
  const isOpen = useAskFocalSidebarStore((s) => s.isOpen);
  const threadKey = useAskFocalSidebarStore((s) => s.threadKey);
  const slideIn = useAskFocalSidebarStore((s) => s.slideIn);
  const close = useAskFocalSidebarStore((s) => s.close);
  const setSlideIn = useAskFocalSidebarStore((s) => s.setSlideIn);
  const { width: screenWidth } = useScreenWidth();
  // Main (left) sidebar — used to auto-collapse on chat drag when chat gets
  // too wide for the current viewport.
  const { state: mainSidebarState, setOpen: setMainSidebarOpen } = useSidebar();

  // At narrow widths, if the chat now dominates the viewport, retract the main
  // sidebar so it doesn't fight the chat for space. Only runs on user-initiated
  // drag — never on programmatic resize.
  const handleDragEnd = useCallback(
    (finalWidth: number) => {
      const vw = window.innerWidth;
      if (
        vw >= AUTO_COLLAPSE_MAIN_SIDEBAR_VIEWPORT_MIN &&
        vw <= AUTO_COLLAPSE_MAIN_SIDEBAR_VIEWPORT_MAX &&
        finalWidth > vw * AUTO_COLLAPSE_MAIN_SIDEBAR_RATIO &&
        mainSidebarState === "expanded"
      ) {
        setMainSidebarOpen(false);
      }
    },
    [mainSidebarState, setMainSidebarOpen],
  );

  // Chat panel width is read directly from the store (clamped to chat's
  // range) and written live during drag. MainLayout's padding-right reads
  // from the same source, so the content edge tracks the cursor with no lag.
  const { width, isDragging, handleProps } = useChatPanelResize({
    onDragEnd: handleDragEnd,
  });

  // Track whether the panel has ever been opened so we can distinguish
  // "never shown yet" (no render) from "closed after being open" (slide-out).
  const [hasOpened, setHasOpened] = useState(false);

  // On open: one-frame defer of `slideIn` so the panel paints at its
  // off-screen position (and main's padding-right paints at 0) before the
  // next frame flips `slideIn=true` → CSS transitions the slide and the
  // padding simultaneously. Close flips `slideIn=false` synchronously in
  // the store so the same transitions run in reverse, symmetrically.
  useEffect(() => {
    if (isOpen) {
      setHasOpened(true);
      const id = requestAnimationFrame(() => setSlideIn(true));
      return () => cancelAnimationFrame(id);
    }
  }, [isOpen, setSlideIn]);

  // Overlay (sheet + backdrop) mode at < lg / 1024px.
  // At ≥1024px the panel renders in push mode (MainLayout reserves the
  // edge space via padding-right on <main>).
  if (screenWidth < 1024) {
    return (
      <>
        {/* Backdrop — fades in on open; removed instantly on close (no exit transition). */}
        {isOpen && (
          <div
            className="fixed inset-0 bg-black/30 z-40 transition-opacity duration-motion-default ease-emphasized"
            onClick={close}
          />
        )}
        {(isOpen || hasOpened) && (
          <div
            className={cn(
              "fixed top-0 right-0 h-screen max-w-[100vw] z-50 shadow-xl",
              CHAT_PANEL_SURFACE,
              !isDragging &&
                "transition-[transform,width] duration-motion-default ease-emphasized",
            )}
            style={{
              width,
              transform: slideIn ? "translateX(0)" : "translateX(100%)",
              pointerEvents: isOpen ? undefined : "none",
            }}
          >
            {threadKey && (
              <div className="flex flex-col h-full relative" style={{ width }}>
                {/* Drag handle — matches ui-kit ResizableHandle visual (bare line) */}
                <div
                  className={CHAT_RESIZE_HANDLE}
                  {...handleProps}
                  role="separator"
                  aria-orientation="vertical"
                  aria-label="Resize chat sidebar"
                  tabIndex={0}
                />
                <SidebarHeader onClose={close} />
                <div className="flex flex-col flex-1 min-h-0 overflow-hidden">
                  <Suspense
                    fallback={
                      <div className="flex items-center justify-center h-full">
                        <LoadingSpinner />
                      </div>
                    }
                  >
                    <AskFocalSidebarContent
                      key={threadKey}
                      threadKey={threadKey}
                    />
                  </Suspense>
                </div>
              </div>
            )}
          </div>
        )}
      </>
    );
  }

  // Push mode for large screens (>= lg / 1024px).
  //
  // The chat panel is purely position:fixed. MainLayout reserves the
  // corresponding edge space via `padding-right` on `<main>`. On the
  // conversation-details page, that page's ConversationLayoutContext
  // hijacks both this panel's `transform` and main's `padding-right` from
  // a single rAF loop (via `data-ask-focal-panel="push"`) so they animate
  // in perfect lockstep alongside the Flows panel's width. On other pages
  // the CSS transition below handles the slide via React-driven style.
  return (
    <>
      {/* Fixed panel — slides in on open, slides out on close. No opacity. */}
      {threadKey && (isOpen || hasOpened) && (
        <div
          data-ask-focal-panel="push"
          className={cn(
            "fixed top-0 right-0 h-screen z-20 overflow-hidden",
            CHAT_PANEL_SURFACE,
            !isDragging &&
              "transition-[transform,width] duration-motion-default ease-emphasized",
            !isOpen && "border-l-0",
          )}
          style={{
            width,
            transform: slideIn ? "translateX(0)" : `translateX(${width}px)`,
            pointerEvents: isOpen ? undefined : "none",
          }}
        >
          <div className="flex flex-col h-full relative">
            {/* Drag handle — matches ui-kit ResizableHandle visual (bare line) */}
            <div
              className={CHAT_RESIZE_HANDLE}
              {...handleProps}
              role="separator"
              aria-orientation="vertical"
              aria-label="Resize chat sidebar"
              tabIndex={0}
            />
            <SidebarHeader onClose={close} />
            <div className="flex flex-col flex-1 min-h-0 overflow-hidden">
              <Suspense
                fallback={
                  <div className="flex items-center justify-center h-full">
                    <LoadingSpinner />
                  </div>
                }
              >
                <AskFocalSidebarContent key={threadKey} threadKey={threadKey} />
              </Suspense>
            </div>
          </div>
        </div>
      )}
    </>
  );
};
